import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router";

import { TextLlm } from "Infrastructure/Api/Api";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useGenerateVoicelinesMutation } from "Projects/Api/Mutations/useGenerateVoicelinesMutation";
import { LlmModelSelect } from "Projects/Components/LlmModelSelect";

interface AiDraftPanelProps {
  onConvertToScript: (text: string) => void;
  onSkipToManual: () => void;
  /** Called before AI generation. Should validate & save answers. Return false to abort. */
  onBeforeGenerate?: () => Promise<boolean>;
  /** Pre-filled artifact text (e.g. from questionnaire generation). Updates in-place on change. */
  initialArtifact?: string;
  /** When true, generation is allowed without user instructions (answers act as input). */
  hasQuestionnaire?: boolean;
}

export const AiDraftPanel = ({
  onConvertToScript,
  onSkipToManual,
  onBeforeGenerate,
  initialArtifact,
  hasQuestionnaire = false,
}: AiDraftPanelProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const { projectId, jobId } = useParams<{
    projectId: string;
    jobId: string;
  }>();

  const [artifact, setArtifact] = useState(initialArtifact ?? "");
  const [instructions, setInstructions] = useState("");

  // Sync artifact when parent-provided initialArtifact changes (e.g. after regenerate-from-questionnaire).
  useEffect(() => {
    if (initialArtifact !== undefined) {
      setArtifact(initialArtifact);
    }
  }, [initialArtifact]);
  const [selectedModel, setSelectedModel] = useState<TextLlm>(
    TextLlm.OpenAI_ChatGpt5,
  );

  const generateMutation = useGenerateVoicelinesMutation(projectId!, jobId!);

  const canGenerate =
    (!!instructions.trim() || hasQuestionnaire) && !generateMutation.isPending;

  const handleGenerate = async () => {
    if (!canGenerate) return;

    // Validate & save answers first (if callback provided)
    if (onBeforeGenerate) {
      const ok = await onBeforeGenerate();
      if (!ok) return;
    }

    const effectiveInstructions =
      instructions.trim() ||
      "Generate the voiceover text based on the provided briefing answers.";

    try {
      const result = await generateMutation.mutateAsync({
        textLlm: selectedModel,
        instructions: effectiveInstructions,
        context: artifact || undefined,
      });
      setArtifact(result.generatedContent);
      setInstructions("");
    } catch {
      // error handled by mutation
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <Stack spacing={Spacing.S}>
      <Box>
        <Typography variant="body1" fontWeight={600}>
          {t(Resources.JobDetail.AiDraftTitle)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t(Resources.JobDetail.AiDraftDescription)}
        </Typography>
      </Box>

      {/* Floating input box */}
      <Box
        sx={(th) => ({
          borderRadius: Radius.M,
          border: `1px solid ${th.colors.border}`,
          backgroundColor: th.palette.background.default,
          boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "border-color 0.15s",
          "&:focus-within": { borderColor: th.palette.primary.main },
        })}
      >
        <Box
          component="textarea"
          value={instructions}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setInstructions(e.target.value)
          }
          onKeyDown={handleKeyDown}
          placeholder={t(Resources.JobDetail.InstructionsPlaceholder)}
          disabled={generateMutation.isPending}
          rows={4}
          sx={{
            resize: "none",
            border: "none",
            backgroundColor: "transparent",
            fontSize: "12px",
            px: 2,
            pt: 2,
            pb: 1,
            "&::placeholder": { color: theme.palette.text.disabled },
            "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
            "&:focus": { outline: "none" },
          }}
        />

        {/* Bottom bar: model selector ← → send button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            pb: 2,
          }}
        >
          <LlmModelSelect value={selectedModel} onChange={setSelectedModel} />

          <IconButton
            onClick={handleGenerate}
            disabled={!canGenerate}
            size="small"
            color="primary"
            sx={(th) => ({
              backgroundColor: th.palette.primary.main,
              color: th.palette.common.white,
              borderRadius: "50%",
              p: 2,
              "&:hover": { backgroundColor: th.palette.primary.dark },
              "&.Mui-disabled": {
                backgroundColor: th.colors.border,
                color: th.palette.text.disabled,
              },
            })}
          >
            {generateMutation.isPending ? (
              <CircularProgress size={15} color="inherit" />
            ) : (
              <ArrowUpwardIcon sx={{ fontSize: 15 }} />
            )}
          </IconButton>
        </Box>
      </Box>

      <Typography variant="body2" fontWeight={600} color="text.secondary">
        {t(Resources.JobDetail.WorkingArtifact)}
      </Typography>

      <Box
        component="textarea"
        value={artifact}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setArtifact(e.target.value)
        }
        placeholder={t(Resources.JobDetail.AiArtifactEmptyState)}
        rows={8}
        sx={{
          resize: "vertical",
          border: `1px solid ${theme.colors.border}`,
          borderRadius: Radius.M,
          padding: theme.spacing(Spacing.S),
          backgroundColor: theme.colors.backgroundInput,
          color: theme.palette.text.primary,
          fontSize: "12px",
          fontFamily: "inherit",
          lineHeight: 1.7,
          outline: "none",
          width: "100%",
          boxSizing: "border-box",
          "&::placeholder": { color: theme.palette.text.disabled },
        }}
      />

      <Stack direction="row" spacing={Spacing.S} justifyContent="space-between">
        <Button
          variant="text"
          color="secondary"
          size="small"
          onClick={onSkipToManual}
          sx={{ fontWeight: 400, fontSize: 12 }}
        >
          {t(Resources.JobDetail.SkipToManualEdit)}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => onConvertToScript(artifact)}
          disabled={!artifact.trim()}
          sx={{ fontWeight: 400, fontSize: 12 }}
        >
          {t(Resources.JobDetail.ConvertToScript)}
        </Button>
      </Stack>
    </Stack>
  );
};
